import { useTheme } from "@emotion/react";
import SearchGradientIcon from 'ui-components/SearchGradientIcon'; // shared black search-field icon
import { Add, EditOutlined, FilterListRounded, Search as SearchIcon } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Chip, Grid, IconButton, Menu, MenuItem, Skeleton, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import BreadcrumbHeader from "layout/BreadcrumbsHeader";
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import AsyncTableListing from "ui-components/AsyncTableListing";
import NeedHelpButton from "ui-components/NeedHelpButton";
import ThemeBGWrapper from "ui-components/ThemeBGWrapper";
import CustomInputField from "ui-components/CustomInputField";
import { LineChart } from "@mui/x-charts/LineChart";
import { ReactComponent as MoreOptionsIcon } from "assets/rounds_icons/more_solid_icon.svg";
import { DeletePortfolio, FetchAllPortfolio } from "api/portfolio";
import { useSelector } from "react-redux";
import DeleteConfirmationDialog from "ui-components/popups/DeleteConfirmationPopup";
import CustomToast from "ui-components/CustomToast";
import toast from "react-hot-toast";
import img1 from "assets/redysyn-logo.png"
import NoPortfolioIcon from "ui-components/icons/NoPortfolioIcon"
import DealFlowListSkeleton from "views/startup_applications/DealFlowListSkeleton";
import Cookies from "js-cookie";
import { hasPermission } from "utils/index";
import { useToast } from "context/ToastContext";
import { Primary } from 'ui-components/Buttons';


/* ------------------------------------------------------------------ *
 * 🔴 PLACEHOLDER DATA — NOT REAL. The API (FetchAllPortfolio) supplies
 * the *_ref formatted strings AND their raw numeric counterparts
 * (total_investment_amount, total_current_investment_amount,
 * total_gain_amount), plus per-row name / founder_email / logo /
 * invested / current value. Everything below still has NO field today:
 *   - the Portfolio Growth series + 1M/6M/1Y/All ranges
 *   - the AI Insights bullets
 *   - the per-row Status chip
 *   - the per-row Gain (amounts arrive pre-formatted as strings like
 *     "₹3.4L", so it can't be computed reliably client-side)
 *   - the per-row "Last Update"
 * Swap each PLACEHOLDER_* for the API when the senior wires it.
 *
 * NOTE: "Current Portfolio Return" is no longer a placeholder - it is
 * computed from the raw numerics below. It is a SIMPLE return
 * (gain / invested), not IRR; IRR would need each investment's date,
 * which the API does not expose.
 * ------------------------------------------------------------------ */

// gain / invested as a 1-dp percentage string. Returns null - so StatCard's
// `value || '-'` renders "-" - when there is nothing meaningful to divide:
// no data yet, or invested is 0, which is the case whenever a portfolio has
// rounds saved with blank amounts. Never yields NaN/Infinity.
const portfolioReturnPercent = (invested, gain) => {
  const inv = Number(invested);
  const g = Number(gain);
  if (!Number.isFinite(inv) || !Number.isFinite(g) || inv === 0) return null;
  return `${((g / inv) * 100).toFixed(1)}%`;
};
const PLACEHOLDER_GROWTH = {
  ranges: ['1M', '6M', '1Y', 'All'],
  months: ["Jun '24", "Jul '24", "Aug '24", "Sep '24", "Oct '24", "Nov '24", "Dec '24"],
  series: [30, 34, 41, 46, 44, 52, 66],
};
const PLACEHOLDER_AI_INSIGHTS = [
  'Your portfolio value increased by 13.1% in the last 6 months.',
  'Fintech accounts for 42% of your total investments.',
  '2 startups are likely raising in the next 90 days.',
  'Your portfolio IRR is 21.6%, higher than Nifty 50 (15.2%).',
];
// Per-row Status / Gain / Last Update, keyed by row index (no API field yet).
const PLACEHOLDER_ROW_META = [
  { status: 'Growing', gain: '₹30L', lastUpdate: '2 days ago' },
  { status: 'Top Performer', gain: '₹1.2Cr', lastUpdate: '3 days ago' },
  { status: 'Early Stage', gain: '₹2L', lastUpdate: '18 days ago' },
  { status: 'Growing', gain: '₹35L', lastUpdate: '12 days ago' },
  { status: 'Update Shared', gain: '₹7L', lastUpdate: '7 days ago' },
  { status: 'Fundraising', gain: '₹2L', lastUpdate: '2 days ago' },
  { status: 'Inactive', gain: '₹0L', lastUpdate: '45 days ago' },
  { status: 'Needs Attention', gain: '-₹6L', lastUpdate: '21 days ago' },
];

// Figma-measured tokens (flagged: read off the design, not exported values)
const P_HEADING = '#101828';
const P_MUTED = '#667085';
const P_BORDER = '#EAECF0';
const P_ICON_BG = '#F4F3FF';
const P_ACCENT = '#6938EF';
const P_GREEN = '#12B76A';
const P_CHIP_BG = '#EEF2FF';

const pCardSx = { bgcolor: '#fff', border: `1px solid ${P_BORDER}`, borderRadius: '12px', boxShadow: 'none' };

// Amounts arrive from the API already formatted ("₹1.27Cr", and negatives as
// "-₹1.5Cr"), so the card takes its currency symbol from the value instead of
// assuming rupees. The leading minus is stripped first - the sign belongs to the
// amount, not to the currency - and the symbol is whatever runs before the digits.
const currencySymbol = (formatted, fallback = '₹') => {
  const text = String(formatted ?? '').trim().replace(/^-/, '');
  const match = text.match(/^[^0-9\s.,]+/);
  return match ? match[0] : fallback;
};

// A formatted amount is negative when it carries a leading minus ("-₹1.5Cr").
const isNegativeAmount = (formatted) => String(formatted ?? '').trim().startsWith('-');

const StatCard = ({ label, value, icon, delta, loading, valueColor }) => (
  <Box sx={{ ...pCardSx, p: 2, height: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: P_ICON_BG, color: P_ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700, fontSize: '0.9rem' }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.78rem', color: P_MUTED, mb: 0.25 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: valueColor || P_HEADING, lineHeight: 1.2 }}>
          {loading ? <Skeleton width={70} /> : (value || '-')}
        </Typography>
        {delta && !loading && (
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: P_GREEN }}>↑ {delta}</Typography>
        )}
      </Box>
    </Box>
  </Box>
);

// The local StatusChip that used to live here is gone: the Status column now passes a
// plain string and AsyncTableListing renders the shared chip, so a second one here only
// produced the nested pill. Colours for these values belong in that shared component's
// statusChipStyle, not in a page-local duplicate.

const columns = [
  { Header: 'Startup Name', accessor: 'name', minWidth: 200, align: 'start', fixed: true },
  // { Header: 'Email', accessor: 'email', minWidth: 150, align: 'start' },
  { Header: 'Stage', accessor: 'stage', minWidth: 100, align: 'center' },
  { Header: 'Category', accessor: 'category', minWidth: 120, align: 'center' },
  // { Header: 'Sector', accessor: 'sector', minWidth: 200, align: 'center' },
  { Header: 'Business Model', accessor: 'business_model', minWidth: 200, align: 'center' },
  { Header: 'Investment Amount', accessor: 'total_investment_amount_ref', minWidth: 100, align: 'center' },
  { Header: 'Current Amount', accessor: 'total_current_investment_amount_ref', minWidth: 100, align: 'center' },
  { Header: 'Actions', accessor: 'actions', minWidth: 50, align: 'right' }
];

const Portfolio = () => {
  const { addToast } = useToast();
  const theme = useTheme();
  const userPermissions = useSelector((state) => state?.permissions?.userPermissions);
  const isNotLargeScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const userDetails = useSelector((state) => state?.investorDetails?.data);
  const navigate = useNavigate()
  const [search, setSearch] = React.useState("");
  // 🔴 PLACEHOLDER: the range tabs have no series API yet — state only, chart data is static.
  const [growthRange, setGrowthRange] = React.useState("6M");
  // `searchText` is a real param on FetchAllPortfolio, so the search box is wired for real.
  const { data: portfolioAllValues, isLoading: isLoadingPortfolio, refetch: refetchPortfolioData } = FetchAllPortfolio({ id: userDetails?._id, searchText: search });

  // null whenever it can't be computed honestly (no data, or invested === 0).
  const returnPercent = portfolioReturnPercent(
    portfolioAllValues?.total_investment_amount,
    portfolioAllValues?.total_gain_amount,
  );

 const portfolioData = portfolioAllValues?.data ? portfolioAllValues?.data?.length > 0 ? portfolioAllValues?.data?.map(item => {
    let startup_info = item?.startup_info;
    let business_model_info = (startup_info ? item?.startup_business_model_info?.config_values : item?.business_model_info?.config_values) || [];
    let category_info = (startup_info ? item?.startup_category_type_info?.config_values : item?.category_type_info?.config_values) || null;
    let stage_info = startup_info ? item?.startup_stage_type_info?.config_values?.name : item?.stage_info?.name;
    return {
      ...item,
      // ...startup_info,
      name: startup_info ? startup_info?.startup_name : item?.startup_name,
      email: item?.founder_email,
      stage: stage_info,
      business_model:
        <>
          {(business_model_info && business_model_info?.length > 0
            ? business_model_info?.length > 1
              ?
              <Tooltip title={business_model_info?.map((item) => <span> {item?.name} <br /></span>)}>
                <Chip style={{ borderRadius: '4px' }} label={business_model_info[0]?.name + " + " + String(business_model_info?.length - 1)} />
              </Tooltip>
              : business_model_info[0]?.name : '-')}
        </>,
      category:
        <>
          {category_info?.name ? category_info?.name : "-"}
        </>,
      image: startup_info ? startup_info?.startup_logo_path : item?.startup_logo_path,
    }
  }) : [] : [];
  // console.log(portfolioData, "portfolioAllValues", portfolioAllValues, "ddddddddddddddddddd")
  // console.log(portfolioData, "ferfegf")
  const {
    mutateAsync: deletePortfolioAsync,
    isLoading: isdeletingReportDocument,
    status: deleteReportDocumentStatus,
    error: deleteReportDocumentError,
    data: deleteReportDocumentResponse
  } = DeletePortfolio();
  const [currentRow, setCurrentRow] = React.useState(null);

  const [openDeletePopup, setOpenDeletePopup] = React.useState(false);


  const handleOpenDeletePopup = (item) => {
    setOpenDeletePopup(true);
    //   console.log(row,"rrrrr")
    setCurrentRow(item)
  }

  const handleCloseDeletePopup = () => {
    setOpenDeletePopup(false);
    setCurrentRow(null)
  }

  const handleDeletion = ({ values }) => {
    deletePortfolioAsync(({ "portfolio_id": currentRow?._id, }), {
      onSuccess: (succ) => {
        // console.log(succ, 'succ')
        addToast("Portfolio Deleted!", "success")
        // toast.success(
        //   <CustomToast message={"Portfolio Deleted!"} style={{ padding: "0px 15px" }} closeIcon={true} closeIconPosition={"center"} />,
        //   {
        //     duration: 2000,
        //     position: "top-right",
        //   }
        // );
        refetchPortfolioData();
        handleCloseDeletePopup();
      },
      onError: (err) => {
        addToast(err?.response?.data?.message, "error")
        // toast.error(
        //   <CustomToast message={err?.response?.data?.message} style={{ padding: "0px 15px" }} closeIcon={true} closeIconPosition={"center"} />,
        //   {
        //     duration: 2000,
        //     position: "top-right",
        //   }
        // );
      }
    });

  }
  // console.log(portfolioData,"listinggg")
  const RowActions = ({ row, iconsColor = theme.palette.customColors.indigo700, index }) => {
    // return <>

    // </>
    // console.log(row, "row")

    const [anchorEl, setAnchorEl] = React.useState(null);
    const openMenu = Boolean(anchorEl);
    const handleClickMenu = (event, index) => {
      setAnchorEl(event.currentTarget);
      // setCurrentIndex(index)
      // console.log(event.currentTarget,"re")
    };
    const handleCloseMenu = (event) => {
      // console.log(event,"event")
      setAnchorEl(null);
      // setCurrentIndex(null);
    };

    // switch (value) {
    //     case "new": return <>
    return hasPermission("portfolio", "edit", userPermissions) || hasPermission("portfolio", "delete", userPermissions) ? <>
      <IconButton
        sx={{ borderRadius: "4px" }}
        id={`more-details-button-${index}`}
        size="small"
        aria-controls={openMenu ? 'more-details-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : undefined}
        onClick={(e) => handleClickMenu(e)}
        centerRipple={false}
      >
        <MoreOptionsIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openMenu}
        aria-controls={openMenu ? 'fade-menu' : 'fade-menu'}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : 'false'}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            padding: "12px",
            borderRadius: "8px",
            "& > ul.MuiList-root": { padding: "0 !important" },
            bgcolor: theme.palette.customColors.g400,
            color: theme.palette.customColors.whiteBG,

          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
      >

        {hasPermission("portfolio", "edit", userPermissions) && <MenuItem disableRipple sx={{ borderBottom: `0px solid ${theme.palette.customColors.g700}`, padding: '0.625rem 1rem' }} onClick={() => { Cookies.set("portfolio_id", row?.original?._id); navigate(`/portfolio/create_portfolio`); }}>
          {/* <DoneRounded sx={{ height: 20, width: 20, color: iconsColor }} />  */}
          Edit
        </MenuItem>}
        {hasPermission("portfolio", "delete", userPermissions) && <MenuItem disableRipple sx={{ borderBottom: `0px solid ${theme.palette.customColors.g700}`, padding: '0.625rem 1rem' }} onClick={() => { handleOpenDeletePopup(row?.original) }}>
          {/* <DoneRounded sx={{ height: 20, width: 20, color: iconsColor }} />  */}
          Delete
        </MenuItem>}
        {hasPermission("portfolio", "edit", userPermissions) && <MenuItem disableRipple sx={{ borderBottom: `0px solid ${theme.palette.customColors.g700}`, padding: '0.625rem 1rem' }} onClick={() => { Cookies.set("portfolio_id", row?.original?._id); navigate(`/portfolio/create_portfolio?page=1&add=true`); }}>
          {/* <DoneRounded sx={{ height: 20, width: 20, color: iconsColor }} />  */}
          Add Round
        </MenuItem>}
      </Menu>
    </> : <>-</>
  }
  const Actions = () => {
    return (
      <Primary onClick={() => {
        hasPermission("portfolio", "add", userPermissions) && Cookies.remove("portfolio_id"); navigate("/portfolio/create_portfolio");
      }} sx={{ padding: '10px 16px', fontWeight: 600, minWidth: '64px', flexShrink: 0, whiteSpace: 'nowrap', '& > .MuiButton-icon.MuiButton-startIcon': { marginRight: '8px', marginLeft: '-4px' } }} startIcon={<Add fontSize='1.25rem' />}>
        {/* Label shown at every breakpoint. It used to be blanked below sm, leaving a
            bare "+" that gave no clue what it did. nowrap + flexShrink:0 keep it on one
            line so the header can't squeeze it. */}
        Add Portfolio
      </Primary>
    )
  }

  const StartupLink = React.memo(({ row, children }) => {

    // console.log(row?.original?.startup_rounds_info,"row")
    return (
      <Stack onClick={() => {
        // console.log(row,"row")
        if (row?.original?.startup_info) {
          Cookies.set("startup_id", row?.original?.startup_info?._id);
          navigate(`/deal-flow/view-profile`)
        }
        else {
          // console.log("Startup Not Found")
        }
      }} style={{ textDecoration: 'none', color: '#616161', fontWeight: 700, cursor: 'pointer' }}>
        {/* <Link to={`view/${row?._id}`} > */}
        {children}
      </Stack>
    )
  })

  return (

    <>
      <DeleteConfirmationDialog open={openDeletePopup} handleClose={handleCloseDeletePopup} handleSubmission={handleDeletion} subheading={"Are you sure you want to delete this portfolio?"} />
      {/* ThemeBGWrapper is position:fixed, so this page couldn't scroll and the table sat
          below the fold unreachable. Scoped to this page via sx; shared component untouched. */}
      <ThemeBGWrapper sx={{ overflowY: 'auto' }}>
        <BreadcrumbHeader
          title={"My Portfolio"}
          breadcrumbTitle="Portfolio"
          customBackgroundColor={theme.palette.customColors.indigo25}
          Actions={hasPermission("portfolio", "add", userPermissions) ? Actions : undefined}
        />
        <Grid item xs={12} container bgcolor={""} padding={"0rem 2rem 0rem 2rem"}>
          {/* The redesigned StatCard widgets, showing only values the API actually
              returns for the current portfolio: invested, current value and gain.
              The old plain cards they replace showed the same three numbers.
              "Current Portfolio Return" stays out - it is a derived percentage whose
              definition (simple return vs IRR) is still undecided. */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard label="Total Invested" icon={currencySymbol(portfolioAllValues?.total_investment_amount_ref)} loading={isLoadingPortfolio} value={portfolioAllValues?.total_investment_amount_ref} />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard label="Current Value" icon={currencySymbol(portfolioAllValues?.total_current_investment_amount_ref)} loading={isLoadingPortfolio} value={portfolioAllValues?.total_current_investment_amount_ref} />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <StatCard
                label="Gain"
                icon={currencySymbol(portfolioAllValues?.total_gain_amount_ref)}
                loading={isLoadingPortfolio}
                value={portfolioAllValues?.total_gain_amount_ref}
                // A loss reads red, a profit green - the plain card this replaced did the same.
                valueColor={
                  portfolioAllValues?.total_gain_amount_ref
                    ? (isNegativeAmount(portfolioAllValues?.total_gain_amount_ref) ? '#F04438' : P_GREEN)
                    : undefined
                }
              />
            </Grid>
          </Grid>

          {/* ---- Portfolio Growth — commented out for now (temporary) per request.
              Restore by removing the `false &&` wrapper below. ---- */}
          {false && (
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} lg={12}>
              <Box sx={{ ...pCardSx, p: 2.5, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: P_HEADING }}>Portfolio Growth</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: P_MUTED, mt: 0.25 }}>Current Value</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: P_HEADING }}>
                        {portfolioAllValues?.total_current_investment_amount_ref || '-'}
                      </Typography>
                      {/* Was the deleted PLACEHOLDER_GAIN_PERCENT. This block is behind
                          `false &&` so it never ran, but the stale reference would have
                          crashed the page the moment anyone re-enabled the growth chart. */}
                      {returnPercent && (
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: P_GREEN }}>↑ {returnPercent}</Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    {PLACEHOLDER_GROWTH.ranges.map((r) => (
                      <Box
                        key={r}
                        onClick={() => setGrowthRange(r)}
                        sx={{
                          px: 1.25, py: '4px', borderRadius: '8px', cursor: 'pointer',
                          border: `1px solid ${growthRange === r ? P_HEADING : P_BORDER}`,
                          fontSize: '0.72rem', fontWeight: 600,
                          color: growthRange === r ? P_HEADING : P_MUTED,
                        }}
                      >
                        {r}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <LineChart
                  height={190}
                  margin={{ top: 16, right: 28, bottom: 24, left: 28 }}
                  xAxis={[{ scaleType: 'point', data: PLACEHOLDER_GROWTH.months }]}
                  yAxis={[{ position: 'none' }]}
                  series={[{ data: PLACEHOLDER_GROWTH.series, color: P_ACCENT, area: true, curve: 'natural', showMark: false }]}
                  slotProps={{ legend: { hidden: true } }}
                  sx={{
                    '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { display: 'none' },
                    '& .MuiChartsAxis-tickLabel': { fontSize: '0.65rem', fill: '#98A2B3' },
                    '& .MuiAreaElement-root': { fill: `${P_ACCENT}22` },
                    '& .MuiLineElement-root': { strokeWidth: 2 },
                  }}
                />
              </Box>
            </Grid>
            {/* AI Insights hidden per request - Portfolio Growth above now spans full width.
                Kept intact (not deleted) so it can be restored.
            <Grid item xs={12} lg={4}>
              <Box sx={{ ...pCardSx, p: 2.5, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: P_HEADING }}>AI Insights</Typography>
                  <Box sx={{ bgcolor: P_ICON_BG, borderRadius: '6px', px: 0.75, py: '2px' }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: P_ACCENT }}>Beta</Typography>
                  </Box>
                </Box>
                <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {PLACEHOLDER_AI_INSIGHTS.map((line) => (
                    <Typography key={line} component="li" sx={{ fontSize: '0.8rem', color: '#475467', lineHeight: 1.5 }}>{line}</Typography>
                  ))}
                </Box>
                <Typography sx={{ mt: 2, fontSize: '0.8rem', fontWeight: 600, color: P_MUTED, cursor: 'pointer' }}>
                  View More Insights
                </Typography>
              </Box>
            </Grid>
            */}
          </Grid>
          )}

          {/* ---- Search + Filter ---- */}
          {/* <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'nowrap' }}> */}
            {/* Fixed-width wrapper: CustomInputField stretches to fill its flex line, which
                pushed the Filter button onto a row of its own. */}
            {/* <Box sx={{ width: { xs: '100%', sm: 320 }, flexShrink: 1, minWidth: 0 }}>
              <CustomInputField
                autoComplete="off"
                fullWidth
                sx={{ marginBottom: 0, '& > .MuiFilledInput-root': { borderColor: `${P_BORDER} !important`, borderRadius: '10px', backgroundColor: '#fff' } }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { setPage(1); refetchPortfolioData(); } }}
                size="small"
                startAdornment={<SearchGradientIcon />}
                placeholder="Search"
              />
            </Box>
          </Grid> */}


          {/* <Grid item xs={12}>  */}
          {isLoadingPortfolio ?
            <Box width={"100%"} maxWidth={"100%"} overflow={"hidden"}>
              <DealFlowListSkeleton columns={columns} rows={limit} showPagination={false} sx={{ borderRadius: '8px', border: `1px solid ${theme.palette.customColors.b50}` }} />
            </Box> :
            <Box width={"100%"} height="auto" maxWidth={"100%"} overflow={"hidden"}>
              <AsyncTableListing
                columns={columns}
                data={portfolioData}
                // data={[]}
                page={page}
                noDataTitle="No Portfolio"
                noDataSubTitle=''
                noDataPlaceHolderImage={NoPortfolioIcon}
                setPage={setPage}
                limit={limit}
                setLimit={setLimit}
                count={portfolioAllValues?.count}
                // count={[]}

                // title={"Investors"}
                // chipText={`${data?.length}${isSmallScreen ? "" : " Investors"}`}
                actions={Actions}
                CustomLink={StartupLink}
                RowActions={RowActions}
                pagination={false}
                sx={{ borderRadius: '8px', border: `1px solid ${theme.palette.customColors.b50}` }}
              // refetchListing={refetchListing}
              />
            </Box>
          }
        </Grid>
        {/* </Grid> */}
      </ThemeBGWrapper>
    </>
  );
};

export default Portfolio;
